import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Lock, ShieldCheck, Folder,
  Mail, User, Globe2, Phone, Loader2, Sparkles, Download
} from "lucide-react";
import { COURSE_FOLDER_ID, SECRETOS, RECURSOS_EXTRA } from "../data/courseVideos";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "manual27_purchased";
const PROFILE_KEY = "curso_access_profile";

const FolderEmbed = ({ folder, color }) => (
  <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
    <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
      <Folder className="h-4 w-4 flex-shrink-0" style={{ color }} />
      <p className="font-bold text-[#1D1D1B] text-sm truncate">{folder.title}</p>
    </div>
    <iframe
      src={`https://drive.google.com/embeddedfolderview?id=${folder.id}#grid`}
      title={folder.title}
      className="w-full block"
      style={{ height: "420px", border: "none" }}
      loading="lazy"
    />
  </div>
);

const SecretoSection = ({ secreto }) => (
  <section data-testid={`secreto-${secreto.num}`} className="scroll-mt-20" id={`secreto-${secreto.num}`}>
    <div
      className="rounded-2xl p-6 mb-5 border-2"
      style={{
        background: `linear-gradient(135deg, ${secreto.color}10, ${secreto.color}05)`,
        borderColor: `${secreto.color}40`,
      }}
    >
      <div className="flex items-start gap-4">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-black text-xl flex-shrink-0"
          style={{ backgroundColor: secreto.color }}
        >
          {secreto.num}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[3px] mb-1" style={{ color: secreto.color }}>
            Secreto {secreto.num}
          </p>
          <h2 className="text-xl sm:text-2xl font-black text-[#1D1D1B] mb-1">{secreto.titulo}</h2>
          {secreto.subtitulo && (
            <p className="text-sm font-semibold text-slate-700 mb-2 italic">{secreto.subtitulo}</p>
          )}
          <p className="text-slate-600 text-sm leading-relaxed">{secreto.descripcion}</p>
          {secreto.pdf_guide && (
            <a
              href={secreto.pdf_guide}
              target="_blank"
              rel="noreferrer"
              data-testid={`pdf-secreto-${secreto.num}`}
              className="inline-flex items-center gap-1.5 mt-3 text-xs font-bold px-3 py-1.5 rounded-lg transition-all hover:scale-[1.02]"
              style={{ backgroundColor: secreto.color, color: "white" }}
            >
              <Download className="h-3.5 w-3.5" /> Descargar guia profesional (PDF)
            </a>
          )}
        </div>
      </div>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-10">
      {secreto.folders.map((f) => <FolderEmbed key={f.id} folder={f} color={secreto.color} />)}
    </div>
  </section>
);

const Gate = ({ onUnlock }) => {
  const [form, setForm] = useState(() => {
    try {
      const saved = localStorage.getItem(PROFILE_KEY);
      return saved ? JSON.parse(saved) : { nombre: "", apellido: "", pais: "", telefono: "", email: "" };
    } catch {
      return { nombre: "", apellido: "", pais: "", telefono: "", email: "" };
    }
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleUnlock = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.nombre.trim() || !form.apellido.trim()) {
      setError("Nombre y apellido son obligatorios.");
      return;
    }
    if (!form.email.trim() || !form.email.includes("@")) {
      setError("Email valido obligatorio.");
      return;
    }
    if (!form.telefono.trim()) {
      setError("Telefono obligatorio para que podamos contactarte.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/curso/access/register`, form);
      localStorage.setItem(STORAGE_KEY, "true");
      localStorage.setItem(PROFILE_KEY, JSON.stringify(form));
      onUnlock();
    } catch (err) {
      setError(err.response?.data?.detail || "No pudimos registrar tu acceso. Intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="course-gate" className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
      <div className="max-w-lg w-full bg-white rounded-2xl border border-slate-200 p-8 shadow-xl">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#c9a227] to-[#e0b835] flex items-center justify-center mx-auto mb-4">
          <Lock className="h-7 w-7 text-[#0a0a14]" />
        </div>
        <h1 className="text-2xl font-black text-[#1D1D1B] mb-2 text-center">Acceso al curso</h1>
        <p className="text-slate-500 text-sm text-center mb-6">
          Completa tus datos para acceder a los videos. Te contactaremos para acompañarte en tu formacion.
        </p>

        <form onSubmit={handleUnlock} className="space-y-3 mb-5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Nombre *</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <input
                  data-testid="gate-nombre"
                  type="text"
                  required
                  value={form.nombre}
                  onChange={(e) => setField("nombre", e.target.value)}
                  placeholder="Carlos"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a227]/40"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Apellido *</label>
              <input
                data-testid="gate-apellido"
                type="text"
                required
                value={form.apellido}
                onChange={(e) => setField("apellido", e.target.value)}
                placeholder="Fernandez"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a227]/40"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Pais *</label>
            <div className="relative">
              <Globe2 className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                data-testid="gate-pais"
                type="text"
                required
                value={form.pais}
                onChange={(e) => setField("pais", e.target.value)}
                placeholder="Mexico"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a227]/40"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Telefono / WhatsApp *</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                data-testid="gate-telefono"
                type="tel"
                required
                value={form.telefono}
                onChange={(e) => setField("telefono", e.target.value)}
                placeholder="+1305..."
                className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a227]/40"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Email *</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                data-testid="gate-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="tu@email.com"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a227]/40"
              />
            </div>
          </div>

          {error && <p className="text-[11px] text-red-600">{error}</p>}

          <button
            type="submit"
            data-testid="gate-submit"
            disabled={submitting}
            className="w-full bg-[#1D1D1B] hover:bg-[#333] disabled:opacity-50 text-white font-bold py-3 rounded-xl text-sm uppercase tracking-wider flex items-center justify-center gap-2"
          >
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Verificando...</> : "Acceder al curso"}
          </button>
        </form>

        <div className="border-t border-slate-100 pt-4 text-center">
          <p className="text-[11px] text-slate-400 mb-2">¿Aun no compraste el manual?</p>
          <Link
            to="/manual27"
            data-testid="gate-buy-cta"
            className="inline-flex items-center gap-1.5 bg-gradient-to-r from-[#c9a227] to-[#e0b835] hover:from-[#e0b835] hover:to-[#c9a227] text-[#0a0a14] font-bold px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider"
          >
            Acceder por $27 USD <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </div>
  );
};

const CursoVideosPage = () => {
  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    document.title = "Videos del curso — Club Viajemos / Travel Empire";
    const has = localStorage.getItem(STORAGE_KEY) === "true";
    setUnlocked(has);
    setChecking(false);
  }, []);

  if (checking) return null;
  if (!unlocked) return <Gate onUnlock={() => setUnlocked(true)} />;

  return (
    <div data-testid="curso-videos-page" className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/" data-testid="back-home" className="flex items-center gap-1 text-slate-500 hover:text-[#1D1D1B] text-sm font-medium">
            <ArrowLeft className="h-4 w-4" /> Inicio
          </Link>
          <div className="w-px h-5 bg-slate-200" />
          <p className="text-sm font-semibold text-[#1D1D1B] truncate flex-1">Videos del curso · Los 5 Secretos</p>
        </div>
        {/* Secret nav */}
        <div className="border-t border-slate-100 bg-white">
          <div className="max-w-6xl mx-auto px-4 py-2 overflow-x-auto">
            <div className="flex gap-2 min-w-max">
              {SECRETOS.map((s) => (
                <a
                  key={s.num}
                  href={`#secreto-${s.num}`}
                  data-testid={`nav-secreto-${s.num}`}
                  className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full hover:bg-slate-100 transition-colors whitespace-nowrap"
                  style={{ color: s.color }}
                >
                  <span className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-black" style={{ backgroundColor: s.color }}>{s.num}</span>
                  {s.titulo}
                </a>
              ))}
              <a
                href="#recursos-extra"
                className="text-xs font-bold px-3 py-1.5 rounded-full hover:bg-slate-100 text-slate-600 whitespace-nowrap"
              >
                Recursos extra
              </a>
            </div>
          </div>
        </div>
      </header>

      <section className="bg-gradient-to-br from-[#1D1D1B] via-[#252548] to-[#1D1D1B] text-white">
        <div className="max-w-6xl mx-auto px-4 py-10 md:py-12">
          <div className="inline-flex items-center gap-2 bg-[#c9a227]/15 text-[#c9a227] px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase mb-3">
            <ShieldCheck className="h-3 w-3" /> Acceso exclusivo
          </div>
          <h1 className="text-3xl md:text-5xl font-black leading-tight mb-2 max-w-3xl">
            Los <span className="text-[#c9a227]">5 Secretos</span> en video
          </h1>
          <p className="text-slate-300 text-base max-w-2xl">
            El curso completo de Travel Empire / Club Viajemos. Cada secreto en su seccion para que avances paso a paso, a tu ritmo.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-10">
        {SECRETOS.map((s) => <SecretoSection key={s.num} secreto={s} />)}

        {/* Recursos extra */}
        <section data-testid="recursos-extra" id="recursos-extra" className="scroll-mt-20">
          <div className="rounded-2xl p-6 mb-5 border-2 border-slate-200 bg-white">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-slate-200 flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-7 w-7 text-slate-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[3px] mb-1 text-slate-500">Bonus</p>
                <h2 className="text-xl sm:text-2xl font-black text-[#1D1D1B] mb-1">{RECURSOS_EXTRA.titulo}</h2>
                <p className="text-slate-600 text-sm">{RECURSOS_EXTRA.descripcion}</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {RECURSOS_EXTRA.folders.map((f) => <FolderEmbed key={f.id} folder={f} color="#64748b" />)}
          </div>
        </section>

        <div className="mt-10 bg-gradient-to-r from-[#c9a227]/10 to-[#45A0EE]/10 border border-[#c9a227]/30 rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <Lock className="h-5 w-5 text-[#c9a227] flex-shrink-0" />
          <p className="text-sm text-[#1D1D1B] flex-1">
            <strong>Tip:</strong> mientras vas viendo los videos, abre el <Link to="/protocolo-2026" className="text-[#c9a227] font-bold underline">Protocolo Maestro 2026</Link> para seguir el calendario de cohortes y los KPIs diarios.
          </p>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white mt-10">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center text-xs text-slate-400">
          Tons of Leads LLC · Florida Seller of Travel ST39568 · DBA Viajemos · Folder: {COURSE_FOLDER_ID.slice(0, 8)}...
        </div>
      </footer>
    </div>
  );
};

export default CursoVideosPage;
